import type {
  AdminActivity,
  HardwareCheck, HardwareOverride, HardwareState,
  ModelSpec,
  AdminSettings, AdminSettingsDoc, AdminUser, UiSettings, VendorSnapshots,
  AdminStatus, BreakevenEstimate, CatalogModel, ChatEvent, Freshness, GpuOption,
  ManagedEstimate, ModelComparison, ModelFit, ModelSummary, NewsItem, OptionsComparison, OverTime, RefreshJob,
  OciAiUnitRate, OciDedicatedFit, OciGenAiModel, OciHosting, OciImportedModel,
  OciUnitShape, UseCase,
  ReservedPrice, ReservedCapacity, SelfHostedEstimate, TasksWorkload, BillReading, SnapshotPage, SystemStatus, TokenPrice, VendorHosting, HostingGrid,
  VendorSource, VramEstimate, Whoami, EgressRates, RetentionPlan } from './types'

class ApiError extends Error {
  status: number

  constructor(status: number, message: string) {
    super(message)
    this.status = status
  }
}

/** Where the app is served from ("/llmarena/"); the API sits beneath it. */
/** The site's home, with a trailing slash: "/" on its own domain, "/llmarena/" behind the gateway. One build
 *  serves both, so it is not fixed when the site is built: the server writes it into the page (api/seo.py).
 *  Without that (the development server), it is where this page sits, every page being one level deep. */
const declared = document.querySelector('meta[name="app-base"]')?.getAttribute('content')
const built = import.meta.env.BASE_URL
export const BASE = declared
  ?? (built.startsWith('/') ? (built.endsWith('/') ? built : `${built}/`) : new URL('.', window.location.href).pathname)
const API = `${BASE}api`

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...init?.headers },
  })
  if (!res.ok) {
    let detail = res.statusText
    try {
      const body = await res.json()
      detail = typeof body.detail === 'string' ? body.detail : JSON.stringify(body.detail)
    } catch {
      /* keep statusText */
    }
    throw new ApiError(res.status, detail)
  }
  plainVersionDone()
  return res.json() as Promise<T>
}

/** The plain version of the page the server wrote beside the app (`.ssr`, api/pages.py) is kept, hidden, until the
 *  app has data of its own to show: the first answer from the API takes it away. A reader whose requests never
 *  succeed (a crawler kept from the API) is left with the page's text and not with an empty shell. */
let plainGone = false
function plainVersionDone() {
  if (plainGone) return
  plainGone = true
  document.querySelector('.ssr')?.remove()
}

/** What the calculator sends to compare options (and to export them). */
export interface OptionsRequest {
  canonical: string; options: { provider: string; mode: string; shape?: string }[]
  output_tokens_per_day?: number; input_output_ratio?: number
  tokens_per_second?: number; utilization?: number; precision?: string
  /** per-vendor region choice: "in-country", "any" or a region id */
  regions?: Record<string, string>
  egress_opts?: EgressOptions
  /** the period (Cost over time): months, and a growth rate or a volume to reach */
  months?: number; growth_pct?: number; end_output_tokens_per_day?: number
}

/** Data transfer out: how answers leave the cloud, and any other traffic a month. */
export interface EgressOptions { mode: 'text' | 'streamed' | 'same_region'; extra_gb_per_month?: number }

/** The per-vendor region choice as a GET query: aws=in-country,azure=any */
const regionsQuery = (r?: Record<string, string>) =>
  r ? Object.entries(r).map(([p, v]) => `${p}=${v}`).join(',') : undefined

/** A POST whose answer is a file: the blob and the server's file name. */
async function download(path: string, body: unknown): Promise<{ blob: Blob; filename: string }> {
  const res = await fetch(`${API}${path}`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
  })
  if (!res.ok) {
    let detail = res.statusText
    try { const b = await res.json(); detail = typeof b.detail === 'string' ? b.detail : JSON.stringify(b.detail) } catch { /* keep */ }
    throw new ApiError(res.status, detail)
  }
  const m = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')
  return { blob: await res.blob(), filename: m?.[1] ?? 'report.pdf' }
}

const post = <T,>(path: string, body: unknown) =>
  request<T>(path, { method: 'POST', body: JSON.stringify(body) })
const put = <T,>(path: string, body: unknown) =>
  request<T>(path, { method: 'PUT', body: JSON.stringify(body) })
const del = <T,>(path: string) => request<T>(path, { method: 'DELETE' })

/** A partial settings document: only the keys being changed. */
type SettingsPatch = {
  [K in keyof AdminSettingsDoc]?: NonNullable<AdminSettingsDoc[K]> extends readonly unknown[] ? AdminSettingsDoc[K]
    : NonNullable<AdminSettingsDoc[K]> extends object ? Partial<NonNullable<AdminSettingsDoc[K]>> : AdminSettingsDoc[K]
}

function qs(params: Record<string, string | number | boolean | undefined | null>) {
  const search = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') search.set(k, String(v))
  }
  const s = search.toString()
  return s ? `?${s}` : ''
}

export const api = {
  /** Liveness plus which optional features the server has behind it. */
  health: () => request<{ status: string; chat: boolean; chat_model?: string }>('/health'),

  freshness: () => request<Freshness>('/freshness'),
  dataSummary: () => request<import('./types').DataSummary>('/data/summary'),
  /** a browser's heartbeat: which page it is on */
  /** admin: the Ask agent's usage and cost */
  adminActivity: () => request<AdminActivity>('/admin/activity'),
  /** the running version, its history and this server's deploys */
  version: () => request<import('./components/Version').VersionInfo>('/version'),

  news: (p: { limit?: number; provider?: string; kind?: string; days?: number } = {}) =>
    request<{ results: NewsItem[] }>(`/news${qs(p)}`).then((r) => r.results),
  /** the feed with its two-line summary; items carry score, oci and impact */
  newsFeed: (p: { limit?: number; provider?: string; days?: number } = {}) =>
    request<import('./types').NewsFeed>(`/news${qs(p)}`),
  priceHistory: (p: { provider: string; name: string; region?: string; unit?: string; service_tier?: string; pricing_model?: string }) =>
    request<import('./types').PriceHistory>(`/news/history${qs(p)}`),
  newsletterPdfUrl: (days: number) => `${API}/newsletter/pdf?days=${days}`,

  /** the newsletter: `website` is the field no person fills in */
  subscribe: (email: string, website = '') => post<{ ok: boolean }>('/newsletter', { email, website }),
  unsubscribe: (email: string) => post<{ ok: boolean }>('/newsletter/unsubscribe', { email }),
  adminSubscribers: () => request<{ count: number; results: import('./types').Subscriber[] }>('/admin/subscribers'),
  removeSubscriber: (email: string) => del<{ removed: boolean }>(`/admin/subscribers${qs({ email })}`),
  subscribersCsvUrl: `${API}/admin/subscribers.csv`,
  /** every exported report: who asked, what for, and where its PDF is kept */
  adminReports: () => request<import('./types').ExportedReports>('/admin/reports'),
  deleteReport: (id: number) => del<{ deleted: number; failed: number }>(`/admin/reports/${id}`),
  purgeReports: (older_than_days: number) => post<{ deleted: number; failed: number }>('/admin/reports/purge', { older_than_days }),
  reportsCsvUrl: `${API}/admin/reports.csv`,
  reportPdfUrl: (id: number) => `${API}/admin/reports/${id}/pdf`,
  /** what the server is doing now, its last hours, and what is being asked of the site */
  adminMonitor: () => request<import('./types').ServerLive>('/admin/monitor'),
  adminMonitorHistory: (hours = 24) => request<import('./types').ServerHistory>(`/admin/monitor/history${qs({ hours })}`),
  /** a page made from the data: a model, a cloud, a GPU, a country, or a list of them (path as in the address) */
  explorePage: (path: string) => request<import('./types').ExplorePageData>(`/pages/${path.split('/').map(encodeURIComponent).join('/')}`),
  /** Admin > Automatic pages: the pages made from the data, how many, the newest, the switches */
  adminAutoPages: () => request<import('./types').AutoPagesState>('/admin/auto-pages'),
  /** Admin > Search engines: the controls' state and what is watched; and the site fetched from outside */
  adminSeo: () => request<import('./types').SeoState>('/admin/seo'),
  adminSeoLive: () => request<import('./types').SeoLive>('/admin/seo/live'),
  adminSeoConsole: (engine: 'google' | 'bing', days: number, refresh = false) =>
    request<import('./types').ConsoleState>(`/admin/seo/console?engine=${engine}&days=${days}${refresh ? '&refresh=1' : ''}`),
  adminSeoIndex: (refresh = false) => request<import('./types').SeoIndexState>(`/admin/seo/index${refresh ? '?refresh=1' : ''}`),
  adminSeoCheck: () => request<{ started: boolean }>('/admin/seo/check', { method: 'POST' }),
  adminSeoIndexCheck: () => request<{ started: boolean; detail?: string }>('/admin/seo/index/check', { method: 'POST' }),
  adminSeoBingSitemap: () => request<{ submitted: string; sitemaps: import('./types').SeoSitemap[] }>('/admin/seo/bing/sitemap', { method: 'POST' }),
  adminSeoAnnounce: (all: boolean) => post<{ sent: number; ok: boolean; detail: string }>('/admin/seo/indexnow', { all }),
  /** start or stop Glances to match the switch just saved */
  adminMonitorApply: () => request<{ enabled: boolean; container: string | null }>('/admin/monitor/apply', { method: 'POST' }),
  /** purge the kept samples: all of them, or those older than so many hours */
  purgeMonitorHistory: (olderThanHours = 0) =>
    del<{ removed: number; kept: { samples: number; oldest: number | null } }>(`/admin/monitor/history${qs({ older_than_hours: olderThanHours || undefined })}`),
  adminRequests: (minutes = 60) => request<import('./types').RequestLog>(`/admin/requests${qs({ minutes })}`),
  /** the cloud bill so far this month */
  adminCloudCost: (refresh = false) => request<import('./types').CloudCost>(`/admin/cloud-cost${qs({ refresh: refresh || undefined })}`),
  /** Admin > Website > Trending: places, sources and hidden stories */
  adminTrending: () => request<import('./types').TrendingAdmin>('/admin/trending'),
  /** Admin > Models: every model, its status, and the retirements */
  adminModels: () => request<import('./types').AdminModelsData>('/admin/models'),
  saveRetirements: (b: { auto_hide?: boolean; key?: string; keys?: string[]; decision?: 'same' | 'other' | null; shown?: boolean }) =>
    put<import('./types').AdminModelsData>('/admin/models/retirements', b),
  /** maintenance mode: the setting, whether the site is closed now, the last switches */
  adminMaintenance: () => request<import('./types').MaintenanceState>('/admin/maintenance'),
  saveMaintenance: (b: Partial<Pick<import('./types').MaintenanceState, 'enabled' | 'message' | 'until' | 'reopen'>>) =>
    put<import('./types').MaintenanceState>('/admin/maintenance', b),
  /** today at a glance, and the sidebar's badges */
  adminOverview: () => request<import('./types').AdminOverviewData>('/admin/overview'),
  /** visitor statistics from Umami */
  adminTraffic: (days = 30) => request<import('./types').Traffic>(`/admin/traffic${qs({ days })}`),
  adminTrafficVisits: (days: number, only?: string) => request<import('./types').TrafficVisits>(`/admin/traffic/visits${qs({ days, only })}`),
  adminTrafficVisit: (id: string, days: number) => request<import('./types').TrafficVisit>(`/admin/traffic/visit/${id}${qs({ days })}`),
  adminTrafficPaths: (days: number, from = '/') => request<import('./types').TrafficPaths>(`/admin/traffic/paths${qs({ days, from })}`),
  adminTrafficFunnels: (days: number) => request<import('./types').TrafficFunnels>(`/admin/traffic/funnels${qs({ days })}`),
  adminTrafficCrawlers: (days: number) => request<import('./types').TrafficCrawlers>(`/admin/traffic/crawlers${qs({ days })}`),
  adminTrafficRetention: (days: number) => request<import('./types').TrafficRetention>(`/admin/traffic/retention${qs({ days })}`),
  adminTrafficUtm: (days: number) => request<import('./types').TrafficUtm>(`/admin/traffic/utm${qs({ days })}`),
  adminTrafficOutcomes: (days: number, event: string) => request<import('./types').TrafficOutcomes>(`/admin/traffic/outcomes${qs({ days, event })}`),
  adminTrafficCompare: (days: number) => request<import('./types').TrafficCompare>(`/admin/traffic/compare${qs({ days })}`),
  /** the Contact us form; `website` is the field no person fills in */
  contact: (b: { name: string; email: string; topic: string; message: string; website?: string }) => post<{ ok: boolean }>('/contact', b),
  assessmentCheckout: (b: { name: string; email: string; company: string; brief: string; context: string; website?: string }) =>
    post<{ url: string; order: number }>('/assessment/checkout', b),
  assessmentRequest: (b: { name: string; email: string; company: string; brief: string; context: string; website?: string }) =>
    post<{ ok: boolean; order: number; name: string; email: string }>('/assessment/request', b),
  assessmentCall: (token: string) => request<import('./types').CallInfo>(`/assessment/call/${encodeURIComponent(token)}`),
  chooseCall: (token: string, b: { slot?: number; none?: boolean; note?: string }) =>
    post<import('./types').CallInfo>(`/assessment/call/${encodeURIComponent(token)}`, b),
  offerCall: (id: number, b: { slots: number[]; minutes: number; tz: string; link: string }) =>
    post<{ ok: boolean; mailed: boolean; url: string; request: import('./types').AssessmentOrder }>(`/admin/assessments/${id}/call`, b),
  assessmentStatus: (order: number, session: string) =>
    request<{ order: number; status: import('./types').AssessmentOrder['status']; name: string; email: string; amount_usd: number }>(`/assessment/orders/${order}${qs({ session })}`),
  adminAssessments: () => request<{ results: import('./types').AssessmentOrder[]; open: number; configured: boolean; webhook: boolean
    mail: boolean; last_call_link: string
    totals: { paid_usd: number; paid_count: number; paid_30d_usd: number; paid_30d_count: number; to_deliver: number; free_count: number; unpaid: number
      products: { product: string; label: string; paid_usd: number; paid_count: number }[] }
    offer: { enabled: boolean; free: boolean; price_usd: number | null }; setting: { enabled: boolean; price_usd: number; free: boolean; teams_link: string } }>('/admin/assessments'),
  assessmentDelivered: (id: number) => post<{ ok: boolean }>(`/admin/assessments/${id}/delivered`, {}),
  removeAssessment: (id: number) => request<{ removed: number }>(`/admin/assessments/${id}`, { method: 'DELETE' }),
  simulationReport: (b: { doc: import('./types').SimulationDoc } & import('./components/ExportPdf').ReportFields) => download('/simulations/report', b),
  watchSimulation: (id: string, email: string, edit_key: string, website = '') => post<{ ok: boolean; watching?: string; total_usd?: number }>(`/simulations/${encodeURIComponent(id)}/watch`, { email, edit_key, website }),
  simulationWatches: (id: string, edit_key: string) => request<{ watches: { email: string; since: number; last_total: number | null; notified_at: number | null }[]; mail: boolean }>(`/simulations/${encodeURIComponent(id)}/watches${qs({ edit_key })}`),
  unwatchSimulation: (id: string, email: string, edit_key: string) => request<{ removed: number }>(`/simulations/${encodeURIComponent(id)}/watch`, { method: 'DELETE', body: JSON.stringify({ email, edit_key }), headers: { 'Content-Type': 'application/json' } }),
  optimiseUseCases: () => request<{ use_cases: import('./types').OptimiseUseCase[] }>('/optimise/use-cases').then((r) => r.use_cases),
  optimise: (doc: import('./types').SimulationDoc, applied: string[] | null) => post<import('./types').Optimised>('/optimise', { doc, applied }),
  optimiseReport: (b: { doc: import('./types').SimulationDoc; applied: string[] | null } & import('./components/ExportPdf').ReportFields) => download('/optimise/report', b),
  priceSimulation: (doc: import('./types').SimulationDoc) => post<import('./types').Simulated>('/simulations/price', doc),
  createSimulation: (doc: import('./types').SimulationDoc) => post<{ id: string; edit_key: string }>('/simulations', doc),
  getSimulation: (id: string) => request<import('./types').SimulationRecord>(`/simulations/${encodeURIComponent(id)}`),
  updateSimulation: (id: string, doc: import('./types').SimulationDoc, edit_key: string) =>
    request<import('./types').SimulationRecord>(`/simulations/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify({ doc, edit_key }), headers: { 'Content-Type': 'application/json' } }),
  deleteSimulation: (id: string, edit_key: string) =>
    request<{ removed: number }>(`/simulations/${encodeURIComponent(id)}`, { method: 'DELETE', body: JSON.stringify({ edit_key }), headers: { 'Content-Type': 'application/json' } }),
  feedback: (b: { page: string; up: boolean; note: string; website?: string }) => post<{ ok: boolean }>('/feedback', b),
  adminFeedback: () => request<import('./types').AdminFeedback>('/admin/feedback'),
  /** Admin > Ask conversations: every exchange kept, grouped into conversations */
  adminAsk: () => request<import('./types').AdminAskLog>('/admin/ask'),
  purgeFeedback: (kind: 'thumbs' | 'questions') => request<{ removed: number }>(`/admin/feedback/${kind}`, { method: 'DELETE' }),
  adminMessages: () => request<{ results: import('./types').ContactMessage[]; unread: number; topics: Record<string, string>; forwarding_to: string }>('/admin/messages'),
  markMessageRead: (id: number) => post<{ ok: boolean }>(`/admin/messages/${id}/read`, {}),
  removeMessage: (id: number) => del<{ removed: boolean }>(`/admin/messages/${id}`),
  /** Admin > Newsletter: mail status and past issues; build, test and send an issue */
  adminNewsletter: () => request<import('./types').NewsletterStatus>('/admin/newsletter'),
  newsletterDraft: (b: import('./types').IssueRequest) => post<import('./types').IssueDraft>('/admin/newsletter/draft', b),
  newsletterTest: (b: import('./types').IssueRequest & { to: string }) => post<{ sent: boolean; to: string }>('/admin/newsletter/test', b),
  newsletterSend: (b: import('./types').IssueRequest) => post<{ id: number; recipients: number }>('/admin/newsletter/send', b),

  adminStatus: () => request<AdminStatus>('/admin/status'),

  /** Vendors and the calculator's starting point, as the administrator set them. */
  uiSettings: () => request<UiSettings>('/ui/settings'),
  adminSettings: () => request<AdminSettings>('/admin/settings'),
  adminCatalogueCheck: () => request<import('./types').CatalogueCheck>('/admin/catalogue-check'),
  adminDataChecks: () => request<import('./types').DataChecks>('/admin/data-checks'),
  runCrosscheck: () => post<{ record: unknown; summary: string | null; mismatches: number }>('/admin/data-checks/crosscheck', {}),
  adminAssumedPrices: (provider: string) => request<import('./types').AssumedPrices>(`/admin/data-checks/assumed${qs({ provider })}`),
  saveAdminSettings: (patch: SettingsPatch) => put<AdminSettings>('/admin/settings', patch),
  resetAdminSettings: () => post<AdminSettings>('/admin/settings/reset', {}),
  adminHardware: () => request<HardwareState>('/admin/hardware'),
  startHardwareCheck: (providers: string[], evidence = true) =>
    post<HardwareCheck>('/admin/hardware/check', { providers, evidence }),
  hardwareCheck: (id: string) => request<HardwareCheck>(`/admin/hardware/check/${id}`),
  saveHardware: (b: Omit<HardwareOverride, 'added_by' | 'added_at' | 'match' | 'note'> & { match?: string; note?: string; confirm_nvlink?: boolean }) =>
    post<HardwareState & { saved: HardwareOverride }>('/admin/hardware', b),
  removeHardware: (provider: string, name: string) =>
    del<HardwareState>(`/admin/hardware/${provider}/${encodeURIComponent(name)}`),
  adminPins: () => request<{ providers: VendorSnapshots[] }>('/admin/pins'),
  /** What a retention rule would delete; nothing is deleted. */
  adminRetention: (keep?: number, unit?: string) => request<RetentionPlan>(`/admin/retention${qs({ keep, unit })}`),
  /** Delete the snapshots a retention rule does not keep. */
  adminPurge: (b: { keep: number; unit: string; compact: boolean }) => post<RetentionPlan>('/admin/retention/purge', b),
  pinSnapshot: (provider: string, snapshot_id: number) =>
    post<{ providers: VendorSnapshots[] }>('/admin/pins', { provider, snapshot_id }),
  unpinSnapshot: (provider: string) =>
    del<{ providers: VendorSnapshots[] }>(`/admin/pins/${encodeURIComponent(provider)}`),
  adminUsers: () => request<{ results: AdminUser[]; you: string | null; registration: boolean }>('/admin/users'),
  /** `twoStep`: the account must have two-step sign-in (it sets it up at its first sign-in); `mustChange`: the
   *  password is good for one sign-in, at which the person chooses their own */
  addAdminUser: (username: string, password: string, name: string, phone = '', twoStep = true, mustChange = false) =>
    post<{ username: string }>('/admin/users', { username, password, name, phone, two_step: twoStep, must_change: mustChange }),
  requireTwoStep: (username: string, on: boolean) =>
    put<{ username: string; mfa_required: boolean }>(`/admin/users/${encodeURIComponent(username)}/two-step-required`, { on }),
  editAdminUser: (username: string, name: string, phone: string) =>
    put<{ username: string; name: string; phone: string }>(`/admin/users/${encodeURIComponent(username)}`, { name, phone }),
  approveAdminUser: (username: string) =>
    post<{ username: string; status: string }>(`/admin/users/${encodeURIComponent(username)}/approve`, {}),
  resendConfirmation: (username: string) => post<{ username: string; sent: boolean }>(`/admin/users/${encodeURIComponent(username)}/resend-confirmation`, {}),
  confirmEmail: (username: string) => post<{ username: string; email_verified: boolean }>(`/admin/users/${encodeURIComponent(username)}/confirm-email`, {}),
  /** whether the sign-in page takes requests for an account (open to everyone) */
  adminAccess: () => request<{ registration: boolean; password_min: number; /** the person is told by email when approved */ notifies: boolean }>('/admin/access'),
  /** confirm a request's email address with the link that was sent to it */
  adminVerify: (token: string) => post<{ ok: boolean; email: string; name: string; approved: boolean; already: boolean }>('/admin/verify', { token }),
  adminRegister: (body: { name: string; email: string; phone: string; password: string; website: string }) =>
    post<{ ok: boolean }>('/admin/register', body),
  /** `mustChange`: the account chooses its own at its next sign-in */
  setAdminPassword: (username: string, password: string, mustChange = false) =>
    put<{ username: string; must_change: boolean }>(`/admin/users/${encodeURIComponent(username)}/password`, { password, must_change: mustChange }),
  askNewPassword: (username: string, on: boolean) =>
    put<{ username: string; must_change_password: boolean }>(`/admin/users/${encodeURIComponent(username)}/must-change-password`, { on }),
  lockAdminUser: (username: string) => post<{ username: string; status: string }>(`/admin/users/${encodeURIComponent(username)}/lock`, {}),
  unlockAdminUser: (username: string) => post<{ username: string; status: string }>(`/admin/users/${encodeURIComponent(username)}/unlock`, {}),
  /** the signed-in account's own new password; answers like a sign-in (the two-step setup may come next) */
  changeOwnPassword: (current: string, next: string) => post<Whoami>('/admin/me/password', { current, new: next }),
  removeAdminUser: (username: string) =>
    del<{ removed: string }>(`/admin/users/${encodeURIComponent(username)}`),

  /** Resolves only when the admin guard accepts this client; says as whom. */
  adminWhoami: () => request<Whoami>('/admin/whoami'),
  /** CPU, memory, disk, the API process and health checks. Admin only. */
  adminSystem: () => request<SystemStatus>('/admin/system'),
  /** `code`: the second step, when the first answer said `mfa_required` */
  adminLogin: (username: string, password: string, code?: string) =>
    post<Whoami>('/admin/login', code ? { username, password, code } : { username, password }),
  mfa: () => request<import('./types').MfaState>('/admin/mfa'),
  mfaSetup: (password: string) => post<import('./types').MfaSetup>('/admin/mfa/setup', { password }),
  mfaEnable: (code: string) => post<import('./types').MfaState>('/admin/mfa/enable', { code }),
  mfaRecovery: (password: string, code: string) => post<import('./types').MfaState>('/admin/mfa/recovery', { password, code }),
  mfaDisable: (password: string, code: string) => post<import('./types').MfaState>('/admin/mfa/disable', { password, code }),
  resetMfa: (username: string) => del<{ username: string; mfa: boolean; sets_up_again?: boolean }>(`/admin/users/${encodeURIComponent(username)}/mfa`),
  adminLogout: () => post<Whoami>('/admin/logout', {}),
  snapshots: (page = 1, perPage = 12) =>
    request<SnapshotPage>(`/admin/snapshots${qs({ page, per_page: perPage })}`),

  adminSources: () =>
    request<{ providers: VendorSource[]; note: string; news?: { source: string; role: string; url: string }[] }>('/admin/sources')
      .then((r) => r),

  refresh: (body: {
    providers?: string[]; refresh_models?: boolean
    aws_regions?: string[]; azure_regions?: string[]
  }) => post<RefreshJob>('/admin/refresh', body),

  job: (id: string) => request<RefreshJob>(`/admin/jobs/${encodeURIComponent(id)}`),

  /** The models OCI Generative AI actually offers, and in which mode. */
  ociGenAiModels: (p: {
    category?: string; mode?: string; canonical?: string; include_deprecated?: boolean
  } = {}) =>
    request<{ results: OciGenAiModel[] }>(`/oci/genai/models${qs(p)}`).then((r) => r.results),

  ociUnitShapes: () =>
    request<{ results: OciUnitShape[]; rates: Record<string, OciAiUnitRate | null> }>(
      '/oci/genai/shapes'),

  /** Pretrained catalogue entry and/or import route for one model, one call. */
  ociHosting: (canonical: string) =>
    request<OciHosting>(`/models/${canonical.split('/').map(encodeURIComponent).join('/')}/oci`),

  ociImported: (p: { q?: string; capability?: string } = {}) =>
    request<{ results: OciImportedModel[] }>(`/oci/genai/imported${qs(p)}`).then((r) => r.results),

  ociDedicatedFit: (ociName: string, precision?: string, replicas?: number) =>
    request<OciDedicatedFit>(
      `/oci/genai/models/${encodeURIComponent(ociName)}/fit${qs({ precision, replicas })}`),

  /** Regions with prices per provider; every provider is a key, some empty. */
  regions: (offeringType?: 'token' | 'reserved' | 'gpu_vm') =>
    request<{ results: Record<string, string[]>; in_country: { country: string; regions: Record<string, string[]> } }>(
      `/regions${qs({ offering_type: offeringType })}`)
      .then((r) => r.results),
  /** Regions per vendor plus the in-country mapping, for the region picker. */
  regionsMeta: () =>
    request<{
      results: Record<string, string[]>; in_country: { country: string; regions: Record<string, string[]> }
      /** every country in which some cloud has a region, by two-letter code */
      countries?: Record<string, { name: string; regions: Record<string, string[]> }>
      home_country?: string
    }>('/regions'),

  gpus: (p: {
    gpu_model?: string; min_vram?: number; provider?: string
    region?: string; pricing_model?: string; limit?: number
    fits_model?: string; precision?: string
  }) => request<{ results: GpuOption[] }>(`/gpus${qs(p)}`).then((r) => r.results),

  tokens: (p: {
    model: string; provider?: string; pricing_model?: string
    service_tier?: string; include_unnormalized?: boolean; limit?: number
  }) => request<{ results: TokenPrice[] }>(`/tokens${qs(p)}`).then((r) => r.results),

  reserved: (p: { q: string; provider?: string; limit?: number }) =>
    request<{ results: ReservedPrice[] }>(`/reserved${qs(p)}`).then((r) => r.results),

  models: (openWeightsOnly = false) =>
    request<{ results: ModelSummary[] }>(
      `/models${qs({ open_weights_only: openWeightsOnly })}`,
    ).then((r) => r.results),

  searchModels: (p: {
    q?: string; open_weights_only?: boolean; sold_by_cloud?: boolean
    min_params_b?: number; max_params_b?: number; limit?: number; family?: string
  }) => request<{ results: CatalogModel[] }>(`/models/search${qs(p)}`).then((r) => r.results),

  /** Families in the catalogue with model counts, "Other" last. */
  modelFamilies: (p: { q?: string; sold_by_cloud?: boolean; open_weights_only?: boolean } = {}) =>
    request<{ results: { family: string; count: number }[] }>(`/models/families${qs(p)}`)
      .then((r) => r.results),

  /** Size and native precision of one model (Hugging Face, or the seed). */
  modelSpec: (canonical: string) =>
    request<ModelSpec>(`/models/${canonical.split('/').map(encodeURIComponent).join('/')}/spec`),

  fit: (canonical: string, precision?: string, limitPerVendor = 5, regions?: Record<string, string>, overhead?: number,
        context?: number, concurrency?: number, months?: number) =>
    request<ModelFit>(
      `/models/${canonical.split('/').map(encodeURIComponent).join('/')}/fit${qs({
        precision, limit_per_vendor: limitPerVendor, regions: regionsQuery(regions), overhead, context, concurrency, months,
      })}`,
    ),
  /** The use cases that set context and concurrency. */
  useCases: () => request<{ results: UseCase[] }>('/use-cases').then((r) => r.results),

  /** Every per-token meter for one model: all tiers, regions, directions. */
  modelTokens: (canonical: string, p: {
    provider?: string; service_tier?: string; include_unnormalized?: boolean; limit?: number
  } = {}) =>
    request<{ results: TokenPrice[] }>(
      `/models/${canonical.split('/').map(encodeURIComponent).join('/')}/tokens${qs(p)}`,
    ).then((r) => r.results),

  compare: (canonical: string, pricingModel = 'on_demand', regions?: Record<string, string>) =>
    request<ModelComparison>(
      `/models/${canonical.split('/').map(encodeURIComponent).join('/')}/compare${qs({
        pricing_model: pricingModel, regions: regionsQuery(regions),
      })}`,
    ),

  /** Per-token vs self-hosted vs dedicated cluster on one vendor, one workload. */
  vendorHosting: (b: {
    canonical: string; provider: string
    output_tokens_per_day?: number; input_output_ratio?: number
    tokens_per_second?: number; utilization?: number
    precision?: string; days?: number
    /** a dedicated unit shape key ("import:H100_X2") instead of the suggestion */
    dedicated_shape?: string
    /** a self-hosted instance type instead of the cheapest */
    self_hosted_shape?: string
    regions?: Record<string, string>
    egress_opts?: EgressOptions
  }) => post<VendorHosting>('/compare/hosting/vendor', b),
  /** Every vendor's hosting modes for one model, in one request: the Calculator's option grid.
   *  The per-vendor choices are maps by provider. */
  hostingGrid: (b: {
    canonical: string
    output_tokens_per_day?: number; input_output_ratio?: number
    tokens_per_second?: Record<string, number>; utilization?: number
    precision?: string; days?: number
    dedicated_shape?: Record<string, string>
    self_hosted_shape?: Record<string, string>
    regions?: Record<string, string>
    egress_opts?: EgressOptions
  }) => post<HostingGrid>('/compare/hosting/grid', b),
  /** Tokens a day from a count of work. */
  workloadTasks: (b: { tasks_per_day: number; prompt_tokens: number; answer_tokens: number; calls_per_task?: number; reasoning_share?: number; retry_rate?: number }) =>
    post<TasksWorkload>('/workload/tasks', b),
  /** A usage export, sent as its text, read into tokens a day per model; nothing is stored. */
  workloadBill: (b: { name: string; text: string; days?: number }) => post<BillReading>('/workload/bill', b),
  /** Every vendor's reserved capacity for one model against its own on-demand price, at a busy time. */
  reservedCapacity: (b: {
    canonical: string
    output_tokens_per_day?: number; input_output_ratio?: number
    utilization?: number; term?: 'none' | '1m' | '3m'
    regions?: Record<string, string>; days?: number
  }) => post<ReservedCapacity>('/compare/reserved', b),

  /** Where OCI stands for what a page shows. */

  /** Egress price tiers for one cloud, in one region or the range across all. */
  egress: (provider: string, region?: string | null) =>
    request<EgressRates>(`/egress${qs({ provider, region: region || undefined })}`),

  /** Two or three cloud/mode options side by side, with break-evens. */
  /** The comparison as a PDF for a customer; the chart as drawn on screen goes along. */
  optionsReport: (b: OptionsRequest & {
    title?: string; prepared_for?: string; notes?: string; chart_png?: string
    email?: string; subscribe?: boolean
    /** the Reserved capacity panel's commitment, when the page showed the panel */
    reserved_term?: 'none' | '1m' | '3m'
  }) => download('/compare/options/report', b),

  /** The Compare or Fit page for one model as a PDF. */
  modelReport: (kind: 'compare' | 'fit', b: {
    canonical: string; precision?: string; limit_per_vendor?: number
    regions?: Record<string, string>; overhead_fraction?: number
    context_tokens?: number; concurrency?: number; use_case?: string
    months?: number; growth_pct?: number; end_output_tokens_per_day?: number
    title?: string; prepared_for?: string; prepared_by?: string; notes?: string
    email?: string; subscribe?: boolean; purpose?: string; purpose_note?: string
  }) => download(`/report/${kind}`, b),

  /** the Ask, Trending and Data pages as a PDF: the form's fields and the page's own request */
  report: (kind: 'ask' | 'trending' | 'data', b: object) => download(`/report/${kind}`, b),

  compareOptions: (b: OptionsRequest) => post<OptionsComparison>('/compare/options', b),
  /** One to eight options over a period as the volume grows. */
  costOverTime: (b: OptionsRequest & { months: number }) => post<OverTime>('/cost/over-time', b),

  estimateManaged: (b: {
    input_tokens_per_day: number; output_tokens_per_day: number
    usd_per_1m_input: number; usd_per_1m_output: number; days?: number
  }) => post<ManagedEstimate>('/estimate/managed', b),

  estimateSelfHosted: (b: {
    usd_per_hour: number; tokens_per_second: number
    utilization?: number; instances?: number
  }) => post<SelfHostedEstimate>('/estimate/self-hosted', b),

  estimateBreakeven: (b: {
    usd_per_hour: number; usd_per_1m_input: number
    usd_per_1m_output: number; input_output_ratio?: number
  }) => post<BreakevenEstimate>('/estimate/breakeven', b),

  estimateVram: (b: {
    params_billions: number; precision?: string; overhead_fraction?: number
  }) => post<VramEstimate>('/estimate/vram', b),
}

/**
 * POST /api/chat and yield each server-sent event as it arrives, so tool calls
 * render while the agent is still working.
 */
export interface ChatTurn { role: 'user' | 'assistant'; content: string }

export async function* chat(
  question: string,
  signal?: AbortSignal,
  history: ChatTurn[] = [],
  sessionId?: string,
): AsyncGenerator<ChatEvent> {
  const res = await fetch(`${API}/chat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    // the conversation so far, final answers only; the server keeps the newest
    body: JSON.stringify({ question, history: history.slice(-20), session_id: sessionId }),
    signal,
  })
  if (!res.ok || !res.body) {
    throw new ApiError(res.status, `chat failed: ${res.statusText}`)
  }
  const reader = res.body.pipeThrough(new TextDecoderStream()).getReader()
  let buffer = ''
  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += value
    // SSE frames are separated by a blank line.
    let split
    while ((split = buffer.indexOf('\n\n')) !== -1) {
      const frame = buffer.slice(0, split)
      buffer = buffer.slice(split + 2)
      const data = frame
        .split('\n')
        .filter((l) => l.startsWith('data:'))
        .map((l) => l.slice(5).trim())
        .join('')
      if (!data) continue
      try {
        const parsed = JSON.parse(data)
        if (parsed.type) yield parsed as ChatEvent
      } catch {
        /* ignore keep-alives and partial frames */
      }
    }
  }
}

export { ApiError }
